/**
 * AdminPanel — Notion-inspired full-height sidebar layout.
 *
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  56px navy header                                                 │
 * ├────────────────┬─────────────────────────────────────────────────┤
 * │  220px sidebar │  Scrollable content                              │
 * │                │                                                  │
 * │  MANAGE        │  <UsersTab / MachinesTab / OrgsTab>              │
 * │  👥 Users  ◀  │                                                  │
 * │  ⚙ Machines   │                                                  │
 * │  🏢 Distribs.  │                                                  │
 * └────────────────┴─────────────────────────────────────────────────┘
 *
 * Roles:
 *   oem_admin        → Users | Machines | Distributors
 *   distributor_admin → Users | Machines (read-only)
 */
import React, { useEffect, useState } from 'react';
import { Image, Alert, Badge, ActionIcon } from '@mantine/core';
import { MdPeople, MdPrecisionManufacturing, MdBusiness, MdArrowBack, MdAdminPanelSettings } from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import AtomsLogo from '../assets/AtomsLogo.png';
import UsersTab from './admin/UsersTab';
import MachinesTab from './admin/MachinesTab';
import OrgsTab from './admin/OrgsTab';
import { getMe } from '../API/adminAPI';

const ATOMS_BLUE = '#1F4E79';

// ── Nav item ──────────────────────────────────────────────────────────────────

const NavItem = ({ icon: Icon, label, value, active, onClick }) => (
  <div
    onClick={() => onClick(value)}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '9px 12px',
      borderRadius: 7,
      cursor: 'pointer',
      background: active ? '#e8f0fe' : 'transparent',
      color: active ? '#1a56db' : '#4b5563',
      fontWeight: active ? 600 : 400,
      fontSize: 14,
      marginBottom: 2,
      userSelect: 'none',
      transition: 'background 0.1s, color 0.1s',
    }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f3f4f6'; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
  >
    <Icon size={17} style={{ flexShrink: 0 }} />
    {label}
  </div>
);

// ── AdminPanel ────────────────────────────────────────────────────────────────

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role     = localStorage.getItem('role') || 'user';
  const orgName  = localStorage.getItem('org_name') || '';

  const [meError,    setMeError]    = useState('');
  const [activeTab,  setActiveTab]  = useState(location.state?.tab || 'users');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (role !== 'oem_admin' && role !== 'distributor_admin') {
      navigate('/app/dashboard', { replace: true });
      return;
    }
    getMe().catch((e) => setMeError(e?.response?.data?.error || 'Could not verify admin access'));

    // If returning from a wizard with refresh flag, remount the active tab
    if (location.state?.refresh) {
      if (location.state?.tab) setActiveTab(location.state.tab);
      setRefreshKey((k) => k + 1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (role !== 'oem_admin' && role !== 'distributor_admin') return null;

  const roleLabel = role === 'oem_admin' ? 'OEM Admin' : 'Distributor Admin';
  const roleColor = role === 'oem_admin' ? '#1a56db' : '#0891b2';

  const navItems = [
    { value: 'users',    label: 'Users',     icon: MdPeople },
    { value: 'machines', label: 'Machines',  icon: MdPrecisionManufacturing },
    ...(role === 'oem_admin' ? [{ value: 'distributors', label: 'Distributors', icon: MdBusiness }] : []),
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f7f8f9', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <div style={{
        background: ATOMS_BLUE,
        color: 'white',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        gap: 14,
        flexShrink: 0,
      }}>
        <ActionIcon
          variant="transparent"
          onClick={() => navigate('/app/dashboard')}
          title="Back to App"
          sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: 'white', background: 'rgba(255,255,255,0.1)' } }}
        >
          <MdArrowBack size={20} />
        </ActionIcon>
        <Image src={AtomsLogo} width={28} height={28} fit="contain" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'white', lineHeight: 1.2 }}>Admin Panel</div>
          {orgName && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>{orgName}</div>}
        </div>
        <Badge
          size="sm"
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            fontWeight: 600,
          }}
        >
          {roleLabel}
        </Badge>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar */}
        <div style={{
          width: 220,
          background: '#ffffff',
          borderRight: '1px solid #e8ecf0',
          padding: '24px 12px 20px',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflowY: 'auto',
        }}>
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            padding: '0 10px',
            marginBottom: 8,
          }}>
            Manage
          </div>

          {navItems.map((item) => (
            <NavItem
              key={item.value}
              {...item}
              active={activeTab === item.value}
              onClick={setActiveTab}
            />
          ))}

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Footer */}
          <div
            onClick={() => navigate('/app/dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 12px',
              borderRadius: 7,
              cursor: 'pointer',
              fontSize: 13,
              color: '#9ca3af',
              marginTop: 8,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#4b5563'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
          >
            <MdArrowBack size={15} />
            Back to App
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
          {meError && <Alert color="red" mb="lg">{meError}</Alert>}

          {activeTab === 'users'        && <UsersTab    key={`users-${refreshKey}`} />}
          {activeTab === 'machines'     && <MachinesTab key={`machines-${refreshKey}`} />}
          {activeTab === 'distributors' && role === 'oem_admin' && <OrgsTab key={`orgs-${refreshKey}`} />}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
